import { Button } from '@/components/shadcn/button'
import { Form } from '@/components/shadcn/form'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { SheetClose } from '@/components/shadcn/sheet'
import { cn } from '@/lib/utils'
import { SpinnerIosRegular } from '@fluentui/react-icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import type { DefaultValues } from 'react-hook-form'
import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import { FieldRenderer } from './FieldRenderer'
import { FormValues, InferFormValues, JsonFormSchemaV2 } from './types'
import { cleanupFormData, createDefaultValues, generateZodSchema } from './utils'
import { isFieldVisible } from './fieldVisibility'
import { useReadOnlyFields } from './useReadOnlyFields'
import { useConditionalRuleFields } from './useConditionalRuleFields'
import { IconRefreshOutline } from '@icons/IconRefreshOutline'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'

// Define a type for the imperative handle
export type FormContentHandle = {
  reset: () => void
}

export const FormContentV2 = forwardRef<
  FormContentHandle,
  {
    schema: JsonFormSchemaV2
    submitHandler: (data: InferFormValues<JsonFormSchemaV2>) => void
    resetDefaults?: DefaultValues<FormValues>
    closeSheetOnSubmit?: boolean
    className?: string
    tileFilters?: React.ReactNode
    isSubmitting?: boolean
    resetOnOpen?: boolean
  }
>(function FormContentV2(
  { schema, submitHandler, resetDefaults, closeSheetOnSubmit, className, tileFilters, isSubmitting, resetOnOpen },
  ref
) {
  const { properties, required, title, description, emphasizeHeader, validationMode } = schema
  const zodSchema = generateZodSchema(properties, required || [])

  const defaultValues = useMemo(() => createDefaultValues(properties), [properties])

  const form = useForm<FormValues>({
    resolver: zodResolver(zodSchema),
    defaultValues,
    mode: validationMode,
  })

  const readOnlyFieldIds = useReadOnlyFields(properties, form.control)
  const visibleWhenMatchedIds = useConditionalRuleFields(properties, 'visibleWhen', form.control)

  const resolvedProperties = useMemo(
    () =>
      properties
        .filter((property) => isFieldVisible(property, {}, visibleWhenMatchedIds))
        .map((property) => (readOnlyFieldIds.has(property.id) ? { ...property, readOnly: true } : property)),
    [properties, visibleWhenMatchedIds, readOnlyFieldIds]
  )

  // When a field transitions to read-only or hidden, reset its value to the schema default
  // so stale user input is not silently submitted alongside disabled or hidden fields.
  const prevReadOnlyFieldIds = useRef<Set<string>>(new Set())
  const prevVisibleFieldIds = useRef<Set<string>>(new Set())
  useEffect(() => {
    const prevReadOnly = prevReadOnlyFieldIds.current
    for (const id of readOnlyFieldIds) {
      if (!prevReadOnly.has(id)) {
        const defaultVal = (defaultValues as Record<string, unknown>)[id]
        form.setValue(id as never, (defaultVal ?? '') as never, { shouldValidate: false })
      }
    }
    prevReadOnlyFieldIds.current = readOnlyFieldIds

    const currentVisibleFieldIds = new Set(resolvedProperties.map((property) => property.id))
    const prevVisible = prevVisibleFieldIds.current
    for (const id of prevVisible) {
      if (!currentVisibleFieldIds.has(id)) {
        const defaultVal = (defaultValues as Record<string, unknown>)[id]
        form.setValue(id as never, (defaultVal ?? '') as never, { shouldValidate: false })
      }
    }
    prevVisibleFieldIds.current = currentVisibleFieldIds
  }, [readOnlyFieldIds, resolvedProperties, defaultValues, form])

  const { reset } = form
  const resetToDefaultValues = useCallback(() => reset(defaultValues), [reset, defaultValues])

  // Expose the reset method via useImperativeHandle
  useImperativeHandle(
    ref,
    () => ({
      reset: resetToDefaultValues,
    }),
    [resetToDefaultValues]
  )

  useEffect(() => {
    if (resetOnOpen) {
      resetToDefaultValues()
    }
  }, [resetOnOpen, resetToDefaultValues])

  useEffect(() => {
    if (resetDefaults) {
      reset(resetDefaults)
    }
  }, [resetDefaults, reset])

  // Re-validate when dependency fields (from requireWhen / visibleWhen) change
  const dependencyMap = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const property of properties) {
      const rules = [property.requireWhen, property.visibleWhen].filter(Boolean)
      for (const rule of rules) {
        const groups: Array<Record<string, unknown>> = []
        if (rule!.anyOf) groups.push(rule!.anyOf as unknown as Record<string, unknown>)
        if (rule!.allOf) groups.push(rule!.allOf as unknown as Record<string, unknown>)
        for (const group of groups) {
          for (const depField of Object.keys(group)) {
            if (!map.has(depField)) map.set(depField, new Set<string>())
            map.get(depField)!.add(property.id)
          }
        }
      }
    }
    return map
  }, [properties])

  useEffect(() => {
    if (dependencyMap.size === 0) return
    const subscription = form.watch((_value, info) => {
      const changed = (info as { name?: string } | undefined)?.name
      if (!changed) return
      const targets = dependencyMap.get(changed)
      if (targets && targets.size > 0) {
        void form.trigger()
      }
    })
    return () => {
      const sub = subscription as unknown
      if (sub && typeof (sub as { unsubscribe?: () => void }).unsubscribe === 'function') {
        ;(sub as { unsubscribe: () => void }).unsubscribe()
      } else if (typeof sub === 'function') {
        ;(sub as () => void)()
      }
    }
  }, [form, dependencyMap])

  const onSubmit = (data: FormValues) => {
    const cleanedData = cleanupFormData(data)
    submitHandler(cleanedData as FormValues)
  }

  const submitButton = (
    <div className="flex justify-end">
      {isSubmitting ? (
        <LoadingButton />
      ) : (
        <div className="flex gap-1">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={resetToDefaultValues}
            disabled={isSubmitting}
          >
            <IconRefreshOutline className="mr-2" />
            Reset to defaults
          </Button>
          <ColumnSubmitButton />
        </div>
      )}
    </div>
  )

  const hasHeader = title || tileFilters

  return (
    <div className="flex h-full flex-col">
      {hasHeader && (
        <ComponentHeader emphasize={emphasizeHeader}>
          {title && <ComponentTitle title={title} description={description} />}
          <ComponentActionsContainer>{tileFilters}</ComponentActionsContainer>
        </ComponentHeader>
      )}

      <Form {...form}>
        <ScrollArea className="h-full">
          {/* <form onSubmit={form.handleSubmit(onSubmit)} className={cn('w-full max-w-2xl space-y-8 pt-4', className)}> */}
          <form onSubmit={form.handleSubmit(onSubmit)} className={cn('w-full space-y-8 pr-2 pt-4', className)}>
            {resolvedProperties.map((property) => {
              return <FieldRenderer field={property} control={form.control} variant="column" key={property.id} />
            })}
            {closeSheetOnSubmit && form.formState.isValid ? (
              <SheetClose asChild>{submitButton}</SheetClose>
            ) : (
              submitButton
            )}
          </form>
        </ScrollArea>
      </Form>
    </div>
  )
})

export function LoadingButton() {
  return (
    <div className="flex w-full justify-center">
      <SpinnerIosRegular fontSize={20} className="animate-spin" />
    </div>
  )
}

export function ColumnSubmitButton() {
  return (
    <Button size="sm" type="submit">
      Submit
    </Button>
  )
}
